import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "../i18n/I18nContext";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { MapPicker } from "./MapField";

/**
 * Generic CRUD manager for admin.
 * props:
 *  title, endpoint (e.g. "services"), columns [{key,label,render?}],
 *  fields [{key,label,type,options?,relation?,help?}], relations {key:{endpoint,labelField}}
 * type: text | textarea | number | checkbox | select | image | bilingual | bilingual-textarea | multiselect
 */
export function ResourceManager({ title, subtitle, endpoint, columns, fields, relations = {}, canDelete = true }) {
  const { t, isAr, localize } = useI18n();
  const [rows, setRows] = useState([]);
  const [rel, setRel] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/${endpoint}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Object.entries(relations).forEach(([key, cfg]) => {
      const url = cfg.endpoint.startsWith("/") ? cfg.endpoint : `/${cfg.endpoint}`;
      api.get(url).then((r) => setRel((p) => ({ ...p, [key]: r.data }))).catch(() => {});
    });
  }, []); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm({ active: true }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, form);
      else await api.post(`/admin/${endpoint}`, form);
      toast.success(t("common.save")); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const del = async (row) => {
    if (!window.confirm(isAr ? "تأكيد الحذف؟" : "Confirm delete?")) return;
    try { await api.delete(`/admin/${endpoint}/${row.id}`); toast.success(t("common.delete")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const relLabel = (cfg, item) => cfg.labelField ? (isAr ? item[cfg.labelField + "_ar"] || item[cfg.labelField] : item[cfg.labelField + "_en"] || item[cfg.labelField]) : (isAr ? item.name_ar : item.name_en) || item.id;

  const renderCell = (col, row) => {
    if (col.render) return col.render(row, { isAr, localize });
    const v = row[col.key];
    if (typeof v === "boolean") return v ? <span className="text-green-600">✓</span> : <span className="text-slate-300">—</span>;
    if (col.bilingual) return localize(row, col.key);
    return v ?? "—";
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-xl font-bold text-slate-900">{title}</h1>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div>
        <button onClick={openNew} data-testid={`add-${endpoint}-btn`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 active:scale-95"><Plus className="h-4 w-4" /> {t("common.add")}</button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[600px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500">
            <tr>{columns.map((c) => <th key={c.key} className="px-4 py-3 text-start font-medium">{c.label}</th>)}<th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-testid={`${endpoint}-row-${row.id}`} className="border-b border-border last:border-0">
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700">{renderCell(c, row)}</td>)}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(row)} data-testid={`edit-${row.id}`} className="rounded-md border border-border p-1.5 text-slate-500 hover:border-primary hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                    {canDelete && <button onClick={() => del(row)} data-testid={`delete-${row.id}`} className="rounded-md border border-border p-1.5 text-[hsl(var(--brand-red))] hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid={`${endpoint}-form`}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{editing ? t("common.edit") : t("common.add")} — {title}</h3>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <FieldInput key={f.key} field={f} value={form} setF={setF} rel={rel} relations={relations} relLabel={relLabel} isAr={isAr} />
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{t("common.cancel")}</button>
              <button onClick={save} disabled={busy} data-testid="resource-save-btn" className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? t("common.loading") : t("common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, setF, rel, relations, relLabel, isAr }) {
  const f = field;
  const label = isAr ? f.label_ar || f.label : f.label_en || f.label;
  const base = "mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-primary focus:outline-none";
  if (f.type === "bilingual" || f.type === "bilingual-textarea") {
    const Tag = f.type === "bilingual-textarea" ? "textarea" : "input";
    return (
      <div>
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <Tag dir="rtl" placeholder="عربي" value={value[`${f.key}_ar`] || ""} onChange={(e) => setF(`${f.key}_ar`, e.target.value)} data-testid={`field-${f.key}-ar`} className={base + " mt-0"} rows={3} />
          <Tag dir="ltr" placeholder="English" value={value[`${f.key}_en`] || ""} onChange={(e) => setF(`${f.key}_en`, e.target.value)} data-testid={`field-${f.key}-en`} className={base + " mt-0"} rows={3} />
        </div>
        {f.help && <p className="mt-1 text-xs text-slate-400">{f.help}</p>}
      </div>
    );
  }
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {f.type === "textarea" ? (
        <textarea value={value[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} rows={3} className={base} />
      ) : f.type === "checkbox" ? (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!value[f.key]} onChange={(e) => setF(f.key, e.target.checked)} data-testid={`field-${f.key}`} className="h-4 w-4" /> {isAr ? "مفعّل" : "Enabled"}</label>
      ) : f.type === "select" ? (
        <select value={value[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} className={base}>
          <option value="">—</option>
          {(f.relation ? (rel[f.key] || []) : f.options || []).map((o) => f.relation
            ? <option key={o.id} value={o.id}>{relLabel(relations[f.key], o)}</option>
            : <option key={o.value} value={o.value}>{isAr ? o.label_ar || o.label : o.label_en || o.label}</option>)}
        </select>
      ) : f.type === "multiselect" ? (
        <div className="mt-1 flex flex-wrap gap-2">
          {(rel[f.key] || []).map((o) => {
            const sel = (value[f.key] || []).includes(o.id);
            return <button type="button" key={o.id} onClick={() => setF(f.key, sel ? (value[f.key] || []).filter((x) => x !== o.id) : [...(value[f.key] || []), o.id])} className={`rounded-full border px-3 py-1.5 text-xs ${sel ? "border-primary bg-primary/10 text-primary" : "border-border text-slate-600"}`}>{relLabel(relations[f.key], o)}</button>;
          })}
        </div>
      ) : f.type === "number" ? (
        <input type="number" step="any" value={value[f.key] ?? ""} onChange={(e) => setF(f.key, e.target.value === "" ? "" : parseFloat(e.target.value))} data-testid={`field-${f.key}`} className={base} />
      ) : f.type === "map" ? (
        <div className="mt-1">
          <MapPicker lat={value.lat} lng={value.lng} onPick={(la, ln) => { setF("lat", la); setF("lng", ln); }} />
          <p className="mt-1 text-xs text-slate-400">{isAr ? "اضغط على الخريطة لتحديد موقع الفرع" : "Tap the map to set branch location"} · <span dir="ltr">{value.lat ?? "—"}, {value.lng ?? "—"}</span></p>
        </div>
      ) : f.type === "image" ? (
        <div>
          <input value={value[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} dir="ltr" placeholder="https://..." className={base} />
          {value[f.key] && <img src={value[f.key]} alt="" className="mt-2 h-20 w-32 rounded-lg object-cover" />}
        </div>
      ) : (
        <input value={value[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`field-${f.key}`} className={base} />
      )}
      {f.help && <p className="mt-1 text-xs text-slate-400">{f.help}</p>}
    </div>
  );
}
